import type { Maybe } from '@adornis/base/utilTypes.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';

/**
 * Martinus only keeps the data the LAK needs from 2023 on, so this is the year the calculation
 * starts in when no Stichtag is maintained. Everything up to then belongs into the Übertrag.
 */
export const LAK_FIRST_CREDITED_YEAR = 2023;

/**
 * The year an Übertrag closes when no Stichtag is maintained. It is the year before the calculation
 * starts by default, so such an Übertrag keeps meaning what it always meant: the balance at the
 * moment the portal took the calculation over.
 */
export const LAK_UEBERTRAG_FALLBACK_YEAR = LAK_FIRST_CREDITED_YEAR - 1;

/**
 * The earliest year an Übertrag may close. The Richtlinien introducing the LAK are from
 * 16 January 2012, so no week before 2012 was ever earned and a Stichtag further back would only
 * offer years in which nothing can be calculated anyway.
 */
export const LAK_EARLIEST_UEBERTRAG_YEAR = 2011;

/**
 * The Übertrag always closes a calendar year, because the LAK has to be announced on every 31.12.
 * The calculated part of the timeline therefore opens on the 1st of January after it. The maintained
 * Stichtag decides that on its own, also below the year the calculation starts in by default: that
 * is how a person whose earlier years have to be calculated rather than carried over is set up - an
 * Übertrag of nothing, closing the last year which is not calculated.
 */
export function getLAKCreditStart(previousLAKHoursYear: Maybe<number>): Date {
  return new Date((previousLAKHoursYear ?? LAK_UEBERTRAG_FALLBACK_YEAR) + 1, 0, 1);
}

/**
 * The years an Übertrag can be stated for: from the year the LAK was introduced up to the year which
 * is currently running.
 */
export function getSelectableUebertragYears(today: Date = new Date()): number[] {
  const years: number[] = [];
  const currentYear = toCalendarDay(today).getUTCFullYear();
  for (let year = LAK_EARLIEST_UEBERTRAG_YEAR; year <= currentYear; year++) years.push(year);
  return years;
}
